import { Fragment } from "react";
import type { ReactNode } from "react";
import {
  CalendarOutlined,
  CreditCardOutlined,
  EuroOutlined,
  PercentageOutlined,
  TeamOutlined,
  UserOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { highlightParts } from "../tableCardHighlight";
import type { FactForm, LineItem } from "./planCardFacts";
import type { TableCardFact, TableCardFactKind } from "./tableCardFacts";

// Частите на фактите за сметката в кутийката на масата (`DesktopTableCard`,
// `TableCard`): един факт във вида си (текст / стойност / иконка), „+N",
// редът с факти с черти между тях и невидимият слой, от който
// `measureCardFacts` чете ширините им.

/** Кегелът: „card" — десктоп (12px, под 112px — 11px като на телефон),
 *  „compact" — телефон и таблет (11px). */
type FactSize = "card" | "compact";

const FACT_FORMS: readonly FactForm[] = ["text", "value", "icon"];

// Височината на реда е като на сумата до него (16px / 13px).
const SIZE_CLASS: Record<FactSize, string> = {
  card: "h-4 text-[12px] @max-[112px]/card:h-[13px] @max-[112px]/card:text-[11px]",
  compact: "h-[13px] text-[11px]",
};

// Чертата между фактите на реда — малко под височината на текста.
const DIVIDER_CLASS: Record<FactSize, string> = {
  card: "h-3 @max-[112px]/card:h-2.5",
  compact: "h-2.5",
};

// „+N" под 112px е по-тясно — да стигне между името и сумата на ниската
// кутийка (`planOneLine`).
const MORE_PADDING_CLASS: Record<FactSize, string> = {
  card: "px-1.5 @max-[112px]/card:px-1",
  compact: "px-1.5",
};

// Докато кутийката се мери (`data-measuring`, `useTableCardFit`), местата на
// фактите ги няма — основата е точно кутийката без тях.
const HIDDEN_WHILE_MEASURING = "group-data-[measuring]/tablecard:hidden";

// Всички части на факта (имена, етикети, суми, стойности) са с една дебелина —
// удебелени в кутийката са само името на масата и сумата.
const FACT_WEIGHT = "font-medium";

// Стойностите (% и €) стоят на мястото на иконката — в цвета на иконките
// (акцентът на темата), не в оранжевото на отстъпките, което не се сменя с темата.
const VALUE_CLASS = "shrink-0 leading-[1.2] tabular-nums text-pos-accent";

/** Цветът на текста по вида: имената — тъмни, общите етикети — приглушени. */
const ROW_TEXT_COLOR: Record<TableCardFactKind, string> = {
  reservation: "text-pos-ink2",
  guests: "text-pos-soft2",
  cardNoReceipt: "text-pos-disc-ink",
  deposit: "text-pos-soft2",
  client: "text-pos-ink2",
  lineDiscount: "text-pos-soft2",
  billDiscount: "text-pos-soft2",
};

/** Иконката на факта — видът „само иконки" (и началото на редовете без
 *  стойност). `!` — `.anticon { color: inherit }` на antd е извън Tailwind
 *  layer-ите. `aria-hidden` — английските им етикети („calendar") не са за
 *  името на кутийката; смисълът е в подсказката на факта. */
function FactIcon({ kind }: { kind: TableCardFactKind }) {
  switch (kind) {
    case "reservation":
      return <CalendarOutlined aria-hidden className="!text-pos-accent" />;
    case "guests":
      return <UserOutlined aria-hidden className="!text-pos-accent" />;
    case "cardNoReceipt":
      return <CreditCardOutlined aria-hidden className="!text-pos-disc-ink" />;
    case "deposit":
      return <WalletOutlined aria-hidden className="!text-pos-accent" />;
    case "client":
      return <TeamOutlined aria-hidden className="!text-pos-accent" />;
    case "lineDiscount":
      return <PercentageOutlined aria-hidden className="!text-pos-accent" />;
    case "billDiscount":
      return <EuroOutlined aria-hidden className="!text-pos-accent" />;
  }
}

/** Началото на факта с текст или стойност: стойността (% / €), иначе
 *  иконката (клиент с 0% — иконката на „Клиенти"). */
function FactLead({ fact }: { fact: TableCardFact }) {
  return fact.value ? <span className={VALUE_CLASS}>{fact.value}</span> : <FactIcon kind={fact.kind} />;
}

/** Във вида „стойност" името остава само там, където е единственото нещо
 *  (резервацията, клиент с 0%) — депозитът е със сумата си. */
const valueKeepsName = (fact: TableCardFact) =>
  fact.value === null && fact.textAmount === undefined && fact.textIsName && fact.text !== null;

interface FactViewProps {
  fact: TableCardFact;
  form: FactForm;
  size: FactSize;
  searchTerm: string;
  /** Копието в слоя за мерене — индексът на факта, без подсказка. */
  measureIndex?: number;
  className?: string;
}

/**
 * Един факт във вида си: „text" — с целия текст („4 гости", „50.00 € · име"),
 * „value" — само стойността („4", „50.00 €", „-3.00 €"; резервацията — с
 * името), „icon" — само иконката. Имената се режат с „…"; общите етикети и
 * сумите — никога. Видимият се чете с подсказката си (`role="img"`), копието
 * за мерене — не (там името, което се реже, е белязано с `data-measure-cut`).
 */
export function FactView({ fact, form, size, searchTerm, measureIndex, className = "" }: FactViewProps) {
  const isMeasure = measureIndex !== undefined;
  const showsText = form === "text" && fact.text !== null;
  const nameCuts = showsText ? fact.textIsName : form === "value" && valueKeepsName(fact);
  const cutMark = isMeasure && nameCuts ? "" : undefined;
  // Името на гостите се търси — оцветява се, както името на масата.
  const text = fact.kind === "reservation" ? highlightParts(fact.text ?? "", searchTerm) : fact.text;
  const nameText = (
    <span data-measure-cut={cutMark} className={`min-w-0 truncate leading-[1.2] ${ROW_TEXT_COLOR[fact.kind]}`}>
      {text}
    </span>
  );
  const amount = <span className="shrink-0 leading-[1.2] tabular-nums text-pos-ink2">{fact.textAmount}</span>;

  let body: ReactNode;
  if (form === "icon") {
    body = <FactIcon kind={fact.kind} />;
  } else if (showsText && fact.textAmount !== undefined) {
    body = (
      <>
        <FactLead fact={fact} />
        <span className="flex min-w-0 items-center">
          {amount}
          <span className="shrink-0 leading-[1.2] text-pos-soft2">{" · "}</span>
          {nameText}
        </span>
      </>
    );
  } else if (showsText || nameCuts) {
    body = (
      <>
        <FactLead fact={fact} />
        {nameText}
      </>
    );
  } else {
    body = (
      <>
        <FactLead fact={fact} />
        {fact.textAmount !== undefined && amount}
        {fact.count !== undefined && (
          <span className="leading-[1.2] tabular-nums text-pos-ink2">{fact.count}</span>
        )}
      </>
    );
  }

  const gap = form === "text" ? "gap-1.5" : fact.count !== undefined ? "gap-0.5" : "gap-1";
  return (
    <span
      data-measure-fact={measureIndex}
      data-measure-form={isMeasure ? form : undefined}
      role={isMeasure ? undefined : "img"}
      aria-label={isMeasure ? undefined : fact.title}
      title={isMeasure ? undefined : fact.title}
      className={`flex items-center ${FACT_WEIGHT} ${gap} ${nameCuts ? "min-w-0 max-w-full" : "shrink-0"} ${
        SIZE_CLASS[size]
      } ${isMeasure ? "" : HIDDEN_WHILE_MEASURING} ${className}`}
    >
      {body}
    </span>
  );
}

interface MoreFactsProps {
  count: number;
  /** Подсказките на скритите; null — копието в слоя за мерене. */
  titles: string[] | null;
  size: FactSize;
}

/** „+N" — скритите факти; подсказката ги изрежда по един на ред. На
 *  избраната кутийка (`data-selected`) `bg-pos-line2` ≈ `bg-pos-marked` —
 *  хапчето е с фона на картата. */
function MoreFacts({ count, titles, size }: MoreFactsProps) {
  const { t } = useTranslation();
  const title = titles === null ? undefined : t("ordering.cardInfo.moreTitle", { list: titles.join("\n") });
  return (
    <span
      data-measure-more={titles === null ? "" : undefined}
      role={title ? "img" : undefined}
      aria-label={title}
      title={title}
      className={`flex shrink-0 items-center rounded-full bg-pos-line2 font-semibold leading-[1.2] tabular-nums text-pos-soft2 group-data-[selected]/tablecard:bg-pos-card ${MORE_PADDING_CLASS[size]} ${SIZE_CLASS[size]}`}
    >
      +{count}
    </span>
  );
}

/** Правата черта между два факта на реда. */
function FactDivider({ size }: { size: FactSize }) {
  return <span aria-hidden className={`w-px shrink-0 bg-pos-muted ${DIVIDER_CLASS[size]}`} />;
}

// Редът с факти: отстоянието от двете страни на чертата — на малкия кегел
// (под 112px и на телефон) по-тясно, за да стигнат петте иконки на реда
// (`dividerWidth` на планировчика се мери от същия клас в слоя за мерене).
const LINE_CLASS: Record<FactSize, string> = {
  card: "flex min-w-0 items-center gap-x-1 @max-[112px]/card:gap-x-[3px]",
  compact: "flex min-w-0 items-center gap-x-[3px]",
};

interface FactLineProps {
  items: readonly LineItem[];
  /** Видът на всички факти на реда — един и същ. */
  form: FactForm;
  facts: readonly TableCardFact[];
  /** Скритите в „+N" (индекси във `facts`). */
  hidden: readonly number[];
  size: FactSize;
  searchTerm: string;
  className?: string;
}

/** Фактите на един ред — до сумата или над нея, всички в един вид, с права
 *  черта между тях. */
export function FactLine({ items, form, facts, hidden, size, searchTerm, className = "" }: FactLineProps) {
  return (
    <span className={`${LINE_CLASS[size]} ${HIDDEN_WHILE_MEASURING} ${className}`}>
      {items.map((item, position) => (
        <Fragment key={item}>
          {position > 0 && <FactDivider size={size} />}
          {item === "more" ? (
            <MoreFacts count={hidden.length} titles={hidden.map((index) => facts[index]?.title ?? "")} size={size} />
          ) : (
            facts[item] && <FactView fact={facts[item]} form={form} size={size} searchTerm={searchTerm} />
          )}
        </Fragment>
      ))}
    </span>
  );
}

interface CardFactsMeasureLayerProps {
  facts: readonly TableCardFact[];
  size: FactSize;
  searchTerm: string;
  /** Името на масата — кегелът му (без рязането) и съдържанието; само за
   *  ниската кутийка, където фактите са на неговия ред. */
  nameClassName?: string;
  name?: ReactNode;
  /** Сумата — кегелът ѝ и текстът. */
  sumClassName: string;
  total: string;
}

/**
 * Невидимият слой за мерене: всеки факт във всеки вид, „+N", чертата между
 * фактите (с отстоянията ѝ), името на един ред и сумата — със същите класове
 * за кегел като видимите, затова и със същите прагове на кутийката. Нулевата
 * кутия с overflow-hidden го държи извън скрола на списъка; `w-max` —
 * всичко е в естествената си ширина.
 */
export function CardFactsMeasureLayer({
  facts,
  size,
  searchTerm,
  nameClassName,
  name,
  sumClassName,
  total,
}: CardFactsMeasureLayerProps) {
  return (
    <div
      aria-hidden
      data-card-measure
      className="pointer-events-none invisible absolute left-0 top-0 h-0 w-0 overflow-hidden"
    >
      <div className="flex w-max flex-col items-start whitespace-nowrap">
        {name !== undefined && (
          <span data-measure-name className={nameClassName}>
            {name}
          </span>
        )}
        <span data-measure-sum className={`inline-block ${sumClassName}`}>
          {highlightParts(total, searchTerm)}
        </span>
        <MoreFacts count={facts.length} titles={null} size={size} />
        <span data-measure-divider className={LINE_CLASS[size]}>
          <span />
          <FactDivider size={size} />
          <span />
        </span>
        {facts.map((fact, index) =>
          FACT_FORMS.map((form) => (
            <FactView
              key={`${index}-${form}`}
              fact={fact}
              form={form}
              size={size}
              searchTerm={searchTerm}
              measureIndex={index}
            />
          )),
        )}
      </div>
    </div>
  );
}
